import React, { useState, useEffect, useMemo } from 'react';
import { GameState, DisplayFixture, League, Player } from '../../../domain/entities';
import { View } from '../../../App';
import { MatchPredictionService } from '../../../services/MatchPredictionService';
import { soundService } from '../../../services/SoundService';
import { getAssistantManagerEmoji } from '../../../utils/utilities';
import { getThemeReadableAccentColor } from '../../../utils/theme';
import { calculateZoneStrengths } from '../../../services/MatchAnalysisService';
import { AiService } from '../../../services/AiService';
import { ASSISTANT_MANAGER_LEVELS } from '../../../domain/constants';
import { MiniBalanceOfPlay } from '../../shared/MiniBalanceOfPlay';
import { FormDisplay } from '../../shared/FormDisplay';
import { PlayerFace } from '../../shared/PlayerFace';
import { TeamBadge } from '../../shared/TeamBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faPersonChalkboard, faAward, faSoccerBall, faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { getLeagueColor } from '../../../utils/iconUtils';
import { usePanelViewPreferences } from '../../../hooks/usePanelViewPreferences';

interface NextMatchPanelProps {
    nextFixture: DisplayFixture | undefined;
    gameState: GameState;
    pushView: (view: View) => void;
    onFixtureClick?: (fixture: DisplayFixture) => void;
    isAiPunditEnabled?: boolean;
}

const getCompetitionEmoji = (competition: string, gameState: GameState) => {
    const cupNames = gameState.config.CUP_NAMES;
    if (cupNames.includes(competition)) {
        return competition === cupNames[0] ? (
            <FontAwesomeIcon icon={faTrophy} style={{ color: '#FFD700', filter: 'hue-rotate(0deg)', marginRight: '4px' }} /> // Gold trophy - Pixel Cup
        ) : (
            <FontAwesomeIcon icon={faTrophy} style={{ color: '#C0C0C0', filter: 'hue-rotate(0deg)', marginRight: '4px' }} /> // Silver trophy - Junior Cup
        );
    }
    // League - use trophy with tier-based color
    return <FontAwesomeIcon icon={faTrophy} style={{ color: getLeagueColor(competition, gameState.config.LEAGUE_NAMES), marginRight: '4px' }} />;
};

const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
};


const generateAccuracyDependentAnalysis = (opponentTeam: any, playerTeam: any, accuracy: number): string => {
    // Offline analysis based on team stats - scaled by accuracy
    const avgSkill = opponentTeam.squad.length > 0
        ? Math.round(opponentTeam.squad.reduce((sum: number, p: any) => sum + p.skill, 0) / opponentTeam.squad.length)
        : 0;
    const avgForm = opponentTeam.squad.length > 0
        ? Math.round(opponentTeam.squad.reduce((sum: number, p: any) => sum + p.form, 0) / opponentTeam.squad.length)
        : 0;
    const avgFitness = opponentTeam.squad.length > 0
        ? Math.round(opponentTeam.squad.reduce((sum: number, p: any) => sum + p.fitness, 0) / opponentTeam.squad.length)
        : 0;

    const getPlayersByPosition = (position: string) =>
        opponentTeam.squad.filter((p: any) => p.position === position);

    const getAvgByPosition = (position: string) => {
        const players = getPlayersByPosition(position);
        return players.length > 0
            ? Math.round(players.reduce((sum: number, p: any) => sum + p.skill, 0) / players.length)
            : 0;
    };

    const defAvg = getAvgByPosition('DEF');
    const fwdAvg = getAvgByPosition('FWD');
    const injuries = opponentTeam.squad.filter((p: any) => p.injury?.type === 'Severe').length;
    const lightInjuries = opponentTeam.squad.filter((p: any) => p.injury?.type === 'Light').length;
    const injuredKey = opponentTeam.squad.filter((p: any) => p.injury?.type === 'Severe').slice(0, 3).map((p: any) => p.name);

    if (accuracy >= 0.85) {
        // Elite/Legendary: Full detailed tactical analysis
        let analysis = '';

        // Overall squad with specific skill levels
        if (avgSkill > 75) {
            analysis += `Technically gifted squad (avg ${avgSkill}/100) - we'll need to be sharp. `;
        } else if (avgSkill > 65) {
            analysis += `Solid technically (avg ${avgSkill}/100) - match their passing tempo. `;
        } else if (avgSkill < 50) {
            analysis += `Vulnerable to pressure (avg ${avgSkill}/100) - press high. `;
        } else {
            analysis += `Average quality (avg ${avgSkill}/100). `;
        }

        // Form with specific details
        if (avgForm > 3) {
            analysis += 'On fire recently - expect confidence in possession. ';
        } else if (avgForm > 1) {
            analysis += 'In decent form - they\'ll be confident. ';
        } else if (avgForm < -2) {
            analysis += 'Struggling badly - they\'re fragile mentally. ';
        } else if (avgForm < 0) {
            analysis += 'Inconsistent form - exploit dips in concentration. ';
        }

        // Positional analysis with detail
        if (fwdAvg > defAvg + 5) {
            analysis += `Fwd stronger than Def (Def: ${defAvg}, Fwd: ${fwdAvg}) - counter-attack opportunities. `;
        } else if (defAvg > fwdAvg + 5) {
            analysis += `Strong Def but blunt Fwd - be patient. `;
        }

        // Fitness detail
        if (avgFitness > 75) {
            analysis += `Fresh (${avgFitness}/100) - will press relentlessly. `;
        } else if (avgFitness < 50) {
            analysis += `Depleted fitness (${avgFitness}/100) - tire them out. `;
        } else if (avgFitness < 60) {
            analysis += `Running low (${avgFitness}/100) - exploit fatigue late. `;
        }

        // Injury detail
        if (injuries > 2) {
            analysis += `Heavily depleted (${injuredKey.join(', ')} out). `;
        } else if (injuries > 0) {
            analysis += `Missing ${injuredKey.join(', ')}. `;
        }
        if (lightInjuries > 2) {
            analysis += 'Several knocks - squad rotation affected. ';
        }

        // Full tactical detail
        if (playerTeam) {
            const opponentFormation = opponentTeam.tactics.formation;
            const opponentMentality = opponentTeam.tactics.mentality;
            const opponentDefStyle = opponentTeam.tactics.defensiveStyle;

            if (opponentFormation.startsWith('5')) {
                analysis += `Defensive setup (${opponentFormation}). `;
            } else if (opponentFormation.startsWith('3')) {
                analysis += `Attacking shape (${opponentFormation}) - wings vulnerable. `;
            }

            if (opponentMentality === 'Attacking') {
                analysis += 'Attacking mentality - gaps defensively. ';
            } else if (opponentMentality === 'Defensive') {
                analysis += 'Sitting deep. Be patient. ';
            }

            if (opponentDefStyle === 'High Press') {
                analysis += 'Aggressive press - quick passes to break it. ';
            } else if (opponentDefStyle === 'Deep Block') {
                analysis += 'Compact block - use width. ';
            }
        }

        return analysis.trim() || 'Well-organized outfit.';
    } else if (accuracy >= 0.7) {
        // Experienced: General tactical observations
        let analysis = '';

        if (avgSkill > 70) {
            analysis += 'Strong squad technically. ';
        } else if (avgSkill < 55) {
            analysis += 'Below average quality. ';
        } else {
            analysis += 'Decent squad. ';
        }

        if (avgForm > 2) {
            analysis += 'Good recent form. ';
        } else if (avgForm < -1) {
            analysis += 'Poor form lately. ';
        }

        if (fwdAvg > defAvg + 3) {
            analysis += 'Stronger in attack. ';
        } else if (defAvg > fwdAvg + 3) {
            analysis += 'Strong defensively. ';
        }

        if (avgFitness > 70) {
            analysis += 'Fresh legs. ';
        } else if (avgFitness < 55) {
            analysis += 'Fitness concerns. ';
        }

        if (injuries > 1) {
            analysis += 'Some injury issues. ';
        }

        if (playerTeam) {
            const opponentMentality = opponentTeam.tactics.mentality;
            if (opponentMentality === 'Attacking') {
                analysis += 'Attacking approach. ';
            } else if (opponentMentality === 'Defensive') {
                analysis += 'Defensive setup. ';
            }
        }

        return analysis.trim() || 'Decent opposition.';
    } else {
        // Aspiring: Very vague observations
        if (avgSkill > 70) {
            return 'Strong-looking team. Prepare well.';
        } else if (avgSkill < 50) {
            return 'Weaker team on paper. Should be manageable.';
        } else {
            return 'Average quality opposition.';
        }
    }
};

export const NextMatchPanel: React.FC<NextMatchPanelProps> = ({ nextFixture, gameState, pushView, onFixtureClick, isAiPunditEnabled }) => {
    const [expectedResult, setExpectedResult] = useState<string | null>(null);
    const [oppositionAnalysis, setOppositionAnalysis] = useState<string | null>(null);
    const { isCompact, toggleView } = usePanelViewPreferences();

    const { homeTeam, awayTeam } = useMemo(() => {
        if (!nextFixture) return { homeTeam: null, awayTeam: null };
        const allTeams = Object.values(gameState.leagues).flatMap((l: League) => l.teams);
        return {
            homeTeam: allTeams.find(t => t.name === nextFixture.home),
            awayTeam: allTeams.find(t => t.name === nextFixture.away)
        };
    }, [nextFixture, gameState.leagues]);

    const playerTeam = useMemo(() => {
        const allTeams = Object.values(gameState.leagues).flatMap((l: League) => l.teams);
        return allTeams.find(t => t.name === gameState.playerTeamName) || null;
    }, [gameState.leagues, gameState.playerTeamName]);

    useEffect(() => {
        if (homeTeam && awayTeam) {
            setExpectedResult(MatchPredictionService.getExpectedResult(homeTeam, awayTeam));
        } else {
            setExpectedResult(null);
        }
    }, [homeTeam, awayTeam]);

    // Generate opposition analysis (AI or offline) with accuracy based on assistant manager level
    useEffect(() => {
        const generateAnalysis = async () => {
            if (!nextFixture) {
                setOppositionAnalysis(null);
                return;
            }

            const opponentName = nextFixture.home === gameState.playerTeamName ? nextFixture.away : nextFixture.home;
            const opponentTeam = Object.values(gameState.leagues)
                .flatMap((l: League) => l.teams)
                .find(t => t.name === opponentName);

            const playerTeam = Object.values(gameState.leagues)
                .flatMap((l: League) => l.teams)
                .find(t => t.name === gameState.playerTeamName);

            if (!opponentTeam || !playerTeam) {
                setOppositionAnalysis(null);
                return;
            }

            // Get assistant manager level for accuracy
            const assistantLevel = playerTeam.assistantManager?.level || 1;
            const assistantInfo = ASSISTANT_MANAGER_LEVELS[assistantLevel - 1];
            const accuracy = assistantInfo?.accuracy || 0.6;

            // Use AI if enabled, otherwise use accuracy-dependent offline analysis
            if (isAiPunditEnabled) {
                try {
                    const aiAnalysis = await AiService.generateAssistantManagerTake(opponentTeam as any);
                    setOppositionAnalysis(aiAnalysis);
                } catch (error) {
                    // Fallback to offline if AI fails
                    setOppositionAnalysis(generateAccuracyDependentAnalysis(opponentTeam, playerTeam, accuracy));
                }
            } else {
                setOppositionAnalysis(generateAccuracyDependentAnalysis(opponentTeam, playerTeam, accuracy));
            }
        };

        generateAnalysis();
    }, [nextFixture, gameState, isAiPunditEnabled]);

    const readableHomeColor = useMemo(() => {
        if (!homeTeam) return 'var(--text-color)';
        return getThemeReadableAccentColor(homeTeam.colors.primary);
    }, [homeTeam]);

    const readableAwayColor = useMemo(() => {
        if (!awayTeam) return 'var(--text-color)';
        return getThemeReadableAccentColor(awayTeam.colors.primary);
    }, [awayTeam]);

    // Balance of Play preview
    const balanceData = useMemo(() => {
        if (!nextFixture || !homeTeam || !awayTeam) return null;
        const homeStartingXI = Object.values(homeTeam.tactics.lineup).filter((id): id is number => id !== null);
        const awayStartingXI = Object.values(awayTeam.tactics.lineup).filter((id): id is number => id !== null);
        const homeStrengths = calculateZoneStrengths(homeTeam, homeStartingXI);
        const awayStrengths = calculateZoneStrengths(awayTeam, awayStartingXI);
        return {
            homeColors: homeTeam.colors,
            awayColors: awayTeam.colors,
            homeAttack: homeStrengths.attack,
            homeMid: homeStrengths.mid,
            homeDef: homeStrengths.def,
            awayAttack: awayStrengths.attack,
            awayMid: awayStrengths.mid,
            awayDef: awayStrengths.def
        };
    }, [nextFixture, homeTeam, awayTeam]);

    // Form data for both teams (last 5 matches)
    const formData = useMemo(() => {
        if (!nextFixture) return null;

        const getTeamForm = (teamName: string) => {
            // Find which league this team belongs to (supports cup competitions with teams from different leagues)
            let teamLeague: string | null = null;
            for (const leagueName in gameState.leagues) {
                const league = gameState.leagues[leagueName];
                if (league.teams.some(t => t.name === teamName)) {
                    teamLeague = leagueName;
                    break;
                }
            }

            // Get fixtures from the team's league
            const allFixtures = teamLeague ? Object.values(gameState.fixtures.leagues[teamLeague] || {}).flat() : [];
            const teamFixtures = allFixtures
                .filter(f => (f.home === teamName || f.away === teamName) && f.result && f.week < gameState.currentWeek)
                .sort((a, b) => b.week - a.week)
                .slice(0, 5);

            const results = teamFixtures.map(f => {
                const { result, home, away } = f;
                if (!result) return 'D';
                if (result.homeScore === result.awayScore) return 'D';
                return ((home === teamName && result.homeScore > result.awayScore) ||
                        (away === teamName && result.awayScore > result.homeScore)) ? 'W' : 'L';
            }).reverse() as ('W' | 'D' | 'L')[];

            // Convert to DisplayFixture format with competition and type info
            const displayFixtures: DisplayFixture[] = teamFixtures.reverse().map(f => ({
                ...f,
                competition: teamLeague || gameState.playerLeagueName,
                type: 'league' as const
            }));

            return {
                results,
                fixtures: displayFixtures
            };
        };

        return {
            homeForm: getTeamForm(nextFixture.home),
            awayForm: getTeamForm(nextFixture.away)
        };
    }, [nextFixture, gameState]);

    // League positions
    const leaguePositions = useMemo(() => {
        if (!nextFixture) return null;

        // Helper function to find team's league and position
        const getTeamLeagueInfo = (teamName: string) => {
            for (const leagueName in gameState.leagues) {
                const league = gameState.leagues[leagueName];
                const team = league.teams.find(t => t.name === teamName);
                if (team) {
                    const standings = [...league.teams].sort((a, b) => {
                        if (b.points !== a.points) return b.points - a.points;
                        const gdA = a.goalsFor - a.goalsAgainst;
                        const gdB = b.goalsFor - b.goalsAgainst;
                        if (gdB !== gdA) return gdB - gdA;
                        return b.goalsFor - a.goalsFor;
                    });
                    const position = standings.findIndex(t => t.name === teamName) + 1;
                    return { leagueName, position };
                }
            }
            return null;
        };

        const homeInfo = getTeamLeagueInfo(nextFixture.home);
        const awayInfo = getTeamLeagueInfo(nextFixture.away);

        if (!homeInfo || !awayInfo) return null;

        return {
            homePos: homeInfo.position,
            homeLeague: homeInfo.leagueName,
            awayPos: awayInfo.position,
            awayLeague: awayInfo.leagueName,
            isCupMatch: nextFixture.type === 'cup'
        };
    }, [nextFixture, gameState]);

    // Key opponent player
    const keyOpponentPlayer = useMemo(() => {
        if (!nextFixture) return null;
        const opponentName = nextFixture.home === gameState.playerTeamName ? nextFixture.away : nextFixture.home;
        const opponentTeam = Object.values(gameState.leagues)
            .flatMap((l: League) => l.teams)
            .find(t => t.name === opponentName);

        if (!opponentTeam) return null;

        // Find top scorer or highest skilled player
        const topScorer = [...opponentTeam.squad]
            .filter(p => (p.goalsScored || 0) > 0)
            .sort((a, b) => (b.goalsScored || 0) - (a.goalsScored || 0))[0];

        if (topScorer && topScorer.goalsScored! >= 3) {
            return { player: topScorer, stat: 'goals', value: topScorer.goalsScored!, teamColor: opponentTeam.colors.primary };
        }

        // Otherwise show their best player by skill
        const bestPlayer = [...opponentTeam.squad].sort((a, b) => b.skill - a.skill)[0];
        return { player: bestPlayer, stat: 'skill', value: bestPlayer.skill, teamColor: opponentTeam.colors.primary };
    }, [nextFixture, gameState]);

    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    const handlePlayerClick = (e: React.MouseEvent, player: Player) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const isCompactView = isCompact('nextMatch');

    return (
        <div className="panel dashboard-next-match">
            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Next Match</span>
                <button
                    onClick={() => toggleView('nextMatch')}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        opacity: 0.7,
                        transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                    title={isCompactView ? 'Show full view' : 'Show compact view'}
                >
                    <FontAwesomeIcon icon={isCompactView ? faExpand : faCompress} />
                </button>
            </div>
            <div className="panel-content">
                {nextFixture ? (
                    <>
                        {isCompactView ? (
                            // Compact View
                            <div>
                                <div className="match-card-header">
                                    <div className="match-card-header-meta">{getCompetitionEmoji(nextFixture.competition, gameState)} {nextFixture.competition} - Week {Math.floor(nextFixture.week)}</div>
                                    {leaguePositions && (
                                        <div className="match-card-positions">
                                            {leaguePositions.isCupMatch ? (
                                                <>{leaguePositions.homeLeague} {leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayLeague} {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                            ) : (
                                                <>{leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                            )}
                                        </div>
                                    )}
                                    <div className="fixture-teams next-match-teams">
                                        <button className="team-name-button" onClick={(e) => handleTeamClick(e, nextFixture.home)} style={{ color: readableHomeColor }}>
                                            {homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{nextFixture.home}
                                        </button>
                                        <span className="vs">vs</span>
                                        <button className="team-name-button" onClick={(e) => handleTeamClick(e, nextFixture.away)} style={{ color: readableAwayColor }}>
                                            {awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{nextFixture.away}
                                        </button>
                                    </div>
                                </div>
                                {expectedResult && (
                                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', textAlign: 'center', fontSize: '0.95em' }}>
                                        <div style={{ opacity: 0.7, fontSize: '0.8em', marginBottom: '4px' }}>Prediction</div>
                                        <strong>{expectedResult}</strong>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Full View
                            <>
                        {/* Header Section */}
                        <div className="match-card-header">
                            <div className="match-card-header-meta">{getCompetitionEmoji(nextFixture.competition, gameState)} {nextFixture.competition} - Week {Math.floor(nextFixture.week)}</div>
                            {leaguePositions && (
                                <div className="match-card-positions">
                                    {leaguePositions.isCupMatch ? (
                                        <>{leaguePositions.homeLeague} {leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayLeague} {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                    ) : (
                                        <>{leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                    )}
                                </div>
                            )}
                            <div className="fixture-teams next-match-teams">
                                <button className="team-name-button" onClick={(e) => handleTeamClick(e, nextFixture.home)} style={{ color: readableHomeColor }}>
                                    {homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{nextFixture.home}
                                </button>
                                <span className="vs">vs</span>
                                <button className="team-name-button" onClick={(e) => handleTeamClick(e, nextFixture.away)} style={{ color: readableAwayColor }}>
                                    {awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{nextFixture.away}
                                </button>
                            </div>

                            {/* Team Form Section - Immediately under team names */}
                            <div className="match-card-teams-form">
                                {/* Home Team Form */}
                                {formData?.homeForm && formData.homeForm.results.length > 0 && (
                                    <div className="team-form-column">
                                        <span className="team-form-label">Form (L5):</span>
                                        <FormDisplay
                                            results={formData.homeForm.results}
                                            fixtures={formData.homeForm.fixtures}
                                            onFixtureClick={onFixtureClick}
                                        />
                                    </div>
                                )}
                                {/* Away Team Form */}
                                {formData?.awayForm && formData.awayForm.results.length > 0 && (
                                    <div className="team-form-column">
                                        <span className="team-form-label">Form (L5):</span>
                                        <FormDisplay
                                            results={formData.awayForm.results}
                                            fixtures={formData.awayForm.fixtures}
                                            onFixtureClick={onFixtureClick}
                                        />
                                    </div>
                                )}
                            </div>                            
                        </div>

                        {/* Main Info Section */}
                        <div className="match-card-main">
                            {/* Prediction */}
                            {expectedResult && (
                                <div className="match-card-prediction-section">
                                    <div className="prediction-title"><i className="fas fa-newspaper" style={{ marginRight: '8px' }}></i>Pundit Take</div>
                                    <div className="match-card-prediction"><strong>{expectedResult}</strong></div>
                                </div>
                            )}

                            {/* Balance of Play Preview */}
                            {balanceData && <MiniBalanceOfPlay {...balanceData} />}
                        </div>

                        {/* Assistant Manager Analysis Section */}
                        <div style={{
                            marginTop: '16px',
                        }}>
                            <div style={{
                                backgroundColor: 'var(--secondary-color)',
                                padding: '12px',
                                borderRadius: '4px'
                            }}>
                                <h4 style={{ color: 'var(--accent-text-color)', margin: '0 0 8px 0', fontSize: '0.95em' }}>
                                    <FontAwesomeIcon icon={faPersonChalkboard} style={{ marginRight: '8px' }} />
                                    Assistant Manager Analysis
                                </h4>

                                {/* Key Opponent Player */}
                                {keyOpponentPlayer && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <p style={{ fontSize: '0.85em', opacity: 0.8, marginBottom: '6px' }}>
                                            <i className="fas fa-user-shield" style={{ marginRight: '6px' }}></i>
                                            Key Threat:
                                        </p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9em' }}>
                                            <button className="link-style" onClick={(e) => handlePlayerClick(e, keyOpponentPlayer.player)}>
                                                <PlayerFace player={keyOpponentPlayer.player} teamColor={keyOpponentPlayer.teamColor} />
                                            </button>
                                            <button className="link-style" onClick={(e) => handlePlayerClick(e, keyOpponentPlayer.player)}>
                                                <span style={{ textDecoration: 'underline', color: 'inherit' }}>{keyOpponentPlayer.player.name}</span>
                                            </button>
                                            <span style={{ opacity: 0.7 }}>
                                                {keyOpponentPlayer.stat === 'goals'
                                                    ? <><FontAwesomeIcon icon={faSoccerBall} style={{ marginRight: '4px' }} />{keyOpponentPlayer.value} goals</>
                                                    : `Skill: ${keyOpponentPlayer.value}`
                                                }
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Opposition Analysis */}
                                {oppositionAnalysis && (
                                    <div>
                                        <p style={{ fontSize: '0.85em', opacity: 0.8, marginBottom: '6px' }}>
                                            <i className="fas fa-search" style={{ marginRight: '6px' }}></i>
                                            Opposition Assessment:
                                        </p>
                                        <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.85, lineHeight: 1.5 }}>
                                            {oppositionAnalysis}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Accuracy Footer */}
                            {playerTeam && (
                                <div style={{
                                    padding: '6px 12px',
                                    marginTop: '8px',
                                    borderTop: '1px solid var(--primary-color)',
                                    fontSize: '0.75em',
                                    opacity: 0.6,
                                    fontStyle: 'italic'
                                }}>
                                    {(() => {
                                        const assistantLevel = playerTeam?.assistantManager?.level || 1;
                                        const assistantInfo = ASSISTANT_MANAGER_LEVELS[assistantLevel - 1];
                                        const accuracy = assistantInfo?.accuracy || 0.6;
                                        return accuracy < 0.8 ? 'Limited accuracy with current assistant manager level' : 'Detailed analysis with expert assistant manager';
                                    })()}
                                </div>
                            )}
                        </div>

                            </>
                        )}
                    </>
                ) : <p style={{ opacity: 0.7 }}>No more matches - End of Season approaching.</p>}
            </div>
        </div>
    );
};